'use client';

import { useRef, useState } from 'react';
import type { JSX } from 'react';
import { ChevronDown } from 'lucide-react';
import mixpanel from 'mixpanel-browser';

interface FAQItemProps {
  question: string;
  answer: JSX.Element;
}

const faqList: FAQItemProps[] = [
  {
    question: 'How is this different from our existing LMS?',
    answer: (
      <div>
        <p>
          An LMS is a content library and a completion tracker. QuizWhizzer is
          the training itself: short, game-based sessions employees actually
          complete. Most customers run it alongside their LMS.
        </p>
      </div>
    ),
  },
  {
    question: 'How long does it take to roll out?',
    answer: (
      <div>
        <p>
          Self-serve teams are live the same day.
        </p>
      </div>
    ),
  },
  {
    question: 'Can we use our own training content?',
    answer: (
      <div>
        <p>
          Yes. Bring slide decks, PDFs, or existing content and we will help
          convert them into game-based sessions.
        </p>
      </div>
    ),
  },
  {
    question: 'How does pricing work?',
    answer: (
      <div>
        <p>
          Per active learner per month, with volume discounts above 250 seats.
          Book a demo and we will quote based on your team size and rollout
          plan. No setup fees for self-serve teams.
        </p>
      </div>
    ),
  },
  {
    question: 'Can we try it before talking to sales?',
    answer: (
      <div className="space-y-2 leading-relaxed">
        Yes. Use the &quot;Try it free&quot; button to spin up a corporate
        account in under a minute. Free for teams up to 10 learners, no credit
        card required.
      </div>
    ),
  },
];

const FaqItem = ({ item }: { item: FAQItemProps }) => {
  const accordion = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);

  return (
    <li className="border-b border-ink-900/15 last:border-0">
      <button
        className="relative flex gap-3 items-center w-full py-5 text-left"
        onClick={(e) => {
          e.preventDefault();
          const willOpen = !isOpen;
          if (willOpen) {
            mixpanel.track('business_faq_opened', {
              question: item.question,
            });
          }
          setIsOpen(willOpen);
        }}
        aria-expanded={isOpen}
      >
        <span
          className={`flex-1 font-display font-semibold text-lg md:text-xl ${
            isOpen ? 'text-brand-600' : 'text-ink-900'
          }`}
        >
          {item?.question}
        </span>
        <ChevronDown
          className={`w-5 h-5 text-ink-400 transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      <div
        ref={accordion}
        className="transition-all duration-300 ease-in-out overflow-hidden"
        style={
          isOpen
            ? { maxHeight: accordion?.current?.scrollHeight, opacity: 1 }
            : { maxHeight: 0, opacity: 0 }
        }
      >
        <div className="pb-5 text-ink-600 leading-relaxed">
          {item?.answer}
        </div>
      </div>
    </li>
  );
};

const BusinessFAQ = () => {
  return (
    <section className="bg-paper-100 paper-grain" id="faq">
      <div className="py-20 lg:py-28 px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-20">
          <div className="flex flex-col text-left lg:basis-1/3">
            <p className="font-hand text-brand-600 text-2xl mb-3 -rotate-2 inline-block">
              before you book the call
            </p>
            <h2 className="font-display font-black text-4xl md:text-5xl tracking-[-0.02em] text-ink-900 leading-[1.02]">
              Common
              <br />
              <span className="italic font-normal">questions.</span>
            </h2>
          </div>

          <ul className="lg:basis-2/3">
            {faqList.map((item, i) => (
              <FaqItem key={i} item={item} />
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
};

export default BusinessFAQ;
